import * as THREE from 'three';
import './styles/global.css';
import { CONFIG } from './config';
import { createSceneContext, resizeRenderer, type SceneContext } from './scene/scene-builder';
import { SpiritOrb } from './scene/spirit-orb';
import { ForegroundLeaves } from './scene/jungle/foreground';
import { BackgroundTrees } from './scene/jungle/background';
import { DinosaurSystem } from './scene/dinosaurs/dinosaur-ai';
import { ShockwaveSystem } from './scene/effects/shockwave';
import { Weapons } from './scene/effects/weapons';
import { CorruptionOverlay } from './scene/effects/corruption';
import { CameraInput } from './camera/camera-input';
import { HandDetector } from './camera/hand-detector';
import { GestureDetector, type GestureResult } from './camera/gesture-detector';
import { gameState } from './state/game-state';
import { inputBus } from './state/input-bus';
import { audio } from './audio/shockwave-sfx';
import { BeatClock } from './audio/beat-clock';
import { HUD } from './ui/hud';
import { Intro } from './ui/intro';
import { GestureHint } from './ui/eye-hint';

const canvas = document.getElementById('scene') as HTMLCanvasElement;
const video = document.getElementById('camera') as HTMLVideoElement;
const uiRoot = document.getElementById('ui-root') as HTMLDivElement;
const appRoot = document.getElementById('app') as HTMLDivElement;

const ctx: SceneContext = createSceneContext(canvas);
const orb = new SpiritOrb();
const foreground = new ForegroundLeaves();
const background = new BackgroundTrees();
const dinosaurs = new DinosaurSystem();
const shockwaves = new ShockwaveSystem();
const weapons = new Weapons();
const corruption = new CorruptionOverlay(uiRoot);

ctx.scene.add(orb.group);
ctx.scene.add(foreground.root);
ctx.scene.add(background.root);
ctx.scene.add(dinosaurs.root);
ctx.scene.add(dinosaurs.dangerRings.root);
ctx.scene.add(shockwaves.root);
ctx.scene.add(weapons.root);

weapons.setCenter(orb.basePos);

const hud = new HUD(uiRoot);
const intro = new Intro(uiRoot);
const gestureHint = new GestureHint(uiRoot, video);
gestureHint.setVisible(false);

intro.show(
  '侏罗纪世界 · 穿越',
  `请允许摄像头，开始你的穿越\n\n手势识别（可按 WASD 进行前后移动并击杀目标）\n\n出拳：击退恐龙 · 剪刀手：推植物 · 张手：护盾\n击败 ${CONFIG.run.winDefeatCount} 只恐龙即可通关 · 限时 ${Math.round(CONFIG.run.timeLimitMs / 60000)} 分钟`,
  {
    label: '开始穿越',
    onClick: () => start(),
  },
  {
    label: '键盘模拟（1/2/3 + WASD）',
    onClick: () => start(true),
  }
);

const camInput = new CameraInput(video);
const hand = new HandDetector();
const gesture = new GestureDetector(CONFIG.hand.holdMs);

let running = false;
let keyboardMode = false;
let lastFrame = performance.now();
let lastFpsCheck = performance.now();
let frameCount = 0;
let avgFps = 60;
let lastTrexRoar = 0;
let runEndShown = false;
let stopHeartbeat: (() => void) | null = null;
let lastHeartbeatState: 'normal' | 'warn' | 'crit' = 'normal';
const moveKeys = new Set<string>();
const beatClock = new BeatClock(CONFIG.run.startBpm);

beatClock.onBeat(() => {
  if (!running) return;
  const gs = gameState.get();
  if (gs.isRespawning || gs.isDead || gs.isTimeUp || gs.isRunComplete) return;
  const elapsedMs = performance.now() - gs.startedAt;
  const difficulty = Math.max(0, Math.min(1, elapsedMs / CONFIG.run.difficultyRampMs));
  const bpm = CONFIG.run.startBpm + (CONFIG.run.endBpm - CONFIG.run.startBpm) * difficulty;
  beatClock.setBpm(bpm);
  dinosaurs.triggerBeatSpawn(difficulty);
  hud.onBeat(bpm, difficulty);
});

const CAMERA_OFFSET_Y = 2.4;
const CAMERA_OFFSET_Z = 8.5;
const CAMERA_LOOK_Y = 1.2;
const PLAYER_SPEED = 5.5;
const PLAYER_MAX_DIST = 18;

async function start(useKeyboard: boolean = false): Promise<void> {
  keyboardMode = useKeyboard;
  intro.hide();
  audio.init();
  const musicLoaded = await audio.tryLoadMusicFile();
  if (!musicLoaded) {
    audio.startAmbient();
  }
  gameState.startRun();
  runEndShown = false;
  dinosaurs.totalSpawned = 0;
  for (const d of [...dinosaurs.dinosaurs]) dinosaurs.removeDinosaur(d.id);
  beatClock.reset(performance.now());
  beatClock.setBpm(CONFIG.run.startBpm);
  if (!useKeyboard) {
    try {
      await camInput.start();
      await hand.load();
      running = true;
      gestureHint.setVisible(true);
      inputBus.emit('face_found');
    } catch (err) {
      console.warn('Camera init failed, falling back to keyboard', err);
      intro.show(
        '摄像头不可用',
        '已切换到键盘模拟模式\n按 1 = 出拳 / 按 2 = 剪刀 / 按 3 = 张手\nWASD 移动',
        {
          label: '进入键盘模式',
          onClick: () => {
            intro.hide();
            running = true;
            keyboardMode = true;
            gestureHint.setVisible(false);
          },
        }
      );
    }
  } else {
    running = true;
    keyboardMode = true;
    gestureHint.setVisible(false);
  }
}

inputBus.on('corruption_full', () => {
  audio.playConsumed();
});

inputBus.on('spirit_respawn', () => {
  audio.playRespawn();
});

window.addEventListener('keydown', (e) => {
  const key = e.key.toLowerCase();
  if (['w', 'a', 's', 'd'].includes(key)) {
    moveKeys.add(key);
    e.preventDefault();
    return;
  }
  if (key === 'm') {
    hud.toggleMute();
    return;
  }
  if (!running || !keyboardMode) return;
  if (key === '1') emitGesture('fist');
  if (key === '2') emitGesture('scissors');
  if (key === '3') emitGesture('open_palm');
});

window.addEventListener('keyup', (e) => {
  const key = e.key.toLowerCase();
  moveKeys.delete(key);
});

function emitGesture(name: 'fist' | 'scissors' | 'open_palm'): void {
  inputBus.emit(name);
}

inputBus.on('fist', () => {
  audio.playImpact();
  shockwaves.spawn(
    'impact',
    orb.basePos,
    CONFIG.shockwave.impact.durationMs,
    CONFIG.shockwave.impact.maxRadius,
    CONFIG.colors.impactPulse.getHex(),
    CONFIG.shockwave.impact.knockback
  );
  const { defeatedIds, armoredHits } = dinosaurs.applyShockwave(
    orb.basePos,
    CONFIG.shockwave.impact.maxRadius,
    CONFIG.shockwave.impact.knockback,
    performance.now(),
    true
  );
  if (defeatedIds.length > 0) {
    gameState.incRepelScore(defeatedIds.length);
  }
  if (armoredHits > 0) {
    hud.showHint('⚠ 先用张手破甲再出拳！');
  }
  hud.showHintIfFirst('fist');
});

inputBus.on('scissors', () => {
  audio.playWind();
  shockwaves.spawn(
    'wind',
    orb.basePos,
    CONFIG.shockwave.wind.durationMs,
    CONFIG.shockwave.wind.maxRadius,
    CONFIG.colors.windPulse.getHex(),
    CONFIG.shockwave.wind.knockback
  );
  foreground.pushFromOrb(1, orb.basePos);
  hud.showHintIfFirst('scissors');
});

inputBus.on('open_palm', () => {
  const gs = gameState.get();
  const now = performance.now();
  if (now - gs.shieldLastTrigger < CONFIG.tongue.cooldownMs) return;
  audio.playShield();
  gameState.triggerShield();
  shockwaves.spawn(
    'shield',
    orb.basePos,
    CONFIG.shockwave.shield.durationMs,
    CONFIG.shockwave.shield.maxRadius,
    CONFIG.colors.shieldPulse.getHex(),
    CONFIG.shockwave.shield.knockback
  );
  const { hitIds, armoredHits } = dinosaurs.applyShockwave(
    orb.basePos,
    CONFIG.shockwave.shield.maxRadius,
    CONFIG.shockwave.shield.knockback,
    now,
    false,
    true
  );
  if (hitIds.length > 0) {
    gameState.incRepelScore(hitIds.length);
  }
  if (armoredHits > 0) {
    hud.showHint(`破甲 ${armoredHits} 只，速出拳击杀！`);
  }
  hud.showHintIfFirst('open_palm');
});

inputBus.on('corruption_full', () => {
  if (gameState.get().isRespawning) {
    setTimeout(() => inputBus.emit('spirit_respawn'), CONFIG.corruption.respawnDurationMs);
  }
});

window.addEventListener('resize', () => resizeRenderer(ctx));
resizeRenderer(ctx);

let lastCorruptionEmit = 0;

function applyMovement(deltaMs: number): void {
  let mvX = 0;
  let mvZ = 0;
  if (moveKeys.has('w')) mvZ -= 1;
  if (moveKeys.has('s')) mvZ += 1;
  if (moveKeys.has('a')) mvX -= 1;
  if (moveKeys.has('d')) mvX += 1;
  if (mvX === 0 && mvZ === 0) return;
  const len = Math.hypot(mvX, mvZ);
  mvX /= len;
  mvZ /= len;
  const step = PLAYER_SPEED * (deltaMs * 0.001);
  orb.basePos.x += mvX * step;
  orb.basePos.z += mvZ * step;
  const dist = Math.hypot(orb.basePos.x, orb.basePos.z);
  if (dist > PLAYER_MAX_DIST) {
    orb.basePos.x = (orb.basePos.x / dist) * PLAYER_MAX_DIST;
    orb.basePos.z = (orb.basePos.z / dist) * PLAYER_MAX_DIST;
  }
}

function showRunEnd(win: boolean, defeated: number, timeUsed: number, total: number, death: boolean = false): void {
  if (runEndShown) return;
  runEndShown = true;
  const seconds = Math.round(timeUsed / 1000);
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  const winTarget = CONFIG.run.winDefeatCount;
  let title: string;
  let message: string;
  if (death) {
    title = '你已死亡';
    message = `被精神体吞噬\n击败 ${defeated} 只\n用时 ${mm}:${ss}\n\n按 R 重新开始`;
  } else if (win) {
      title = '是否继续挑战？';
      message = `已击败 ${defeated} 只 · 用时 ${mm}:${ss}\n\n按 R 或点击按钮重新开始`;
    } else {
    title = '时间到';
    message = `时间耗尽\n击败 ${defeated} / ${winTarget}\n用时 ${mm}:${ss}\n\n按 R 重新开始`;
  }
  intro.show(
    title,
    message,
    {
      label: win ? '确定' : '再来一次 (R)',
      onClick: () => restartRun(),
    }
  );
}

function restartRun(): void {
  intro.hide();
  gameState.startRun();
  runEndShown = false;
  dinosaurs.totalSpawned = 0;
  for (const d of [...dinosaurs.dinosaurs]) dinosaurs.removeDinosaur(d.id);
  running = true;
  beatClock.reset(performance.now());
  beatClock.setBpm(CONFIG.run.startBpm);
  if (stopHeartbeat) {
    stopHeartbeat();
    stopHeartbeat = null;
    lastHeartbeatState = 'normal';
  }
}

window.addEventListener('keydown', (e) => {
  if (e.key === 'r' || e.key === 'R') {
    if (runEndShown) restartRun();
  }
});

inputBus.on('run_complete', () => {
  const gs = gameState.get();
  showRunEnd(true, gs.dinosaursDefeated, gs.elapsedMs, gs.totalDinosaurs);
});
inputBus.on('time_up', () => {
  const gs = gameState.get();
  showRunEnd(false, gs.dinosaursDefeated, gs.elapsedMs, gs.totalDinosaurs);
});

inputBus.on('player_died', () => {
  if (runEndShown) return;
  if (stopHeartbeat) {
    stopHeartbeat();
    stopHeartbeat = null;
    lastHeartbeatState = 'normal';
  }
  audio.playConsumed();
  setTimeout(() => {
    if (runEndShown) return;
    const gs = gameState.get();
    showRunEnd(false, gs.dinosaursDefeated, gs.elapsedMs, gs.totalDinosaurs, true);
  }, 700);
});

function frame(now: number): void {
  requestAnimationFrame(frame);
  if (!running) {
    ctx.renderer.render(ctx.scene, ctx.camera);
    return;
  }
  const deltaMs = Math.min(50, now - lastFrame);
  lastFrame = now;
  frameCount++;
  if (now - lastFpsCheck > 5000) {
    avgFps = (frameCount * 1000) / (now - lastFpsCheck);
    frameCount = 0;
    lastFpsCheck = now;
  }

  applyMovement(deltaMs);

  beatClock.update(now);

  const targetCamX = orb.basePos.x;
  const targetCamY = CAMERA_OFFSET_Y;
  const targetCamZ = orb.basePos.z + CAMERA_OFFSET_Z;
  ctx.camera.position.set(targetCamX, targetCamY, targetCamZ);

  let handData = null;
  let gestureResult: GestureResult = {
    gesture: 'unknown',
    confidence: 0,
    fingerState: { thumb: false, index: false, middle: false, ring: false, pinky: false },
    rawDistances: { thumbTip: 0, indexTip: 0, middleTip: 0, ringTip: 0, pinkyTip: 0 },
  };

  if (!keyboardMode) {
    handData = hand.detect(video, now);
    if (handData) {
      const result = gesture.detect(handData.landmarks[0] ?? null, now);
      if (result.gesture !== 'unknown') {
        gestureResult = result;
        if (!gesture.isOnCooldown(now, CONFIG.hand.cooldownMs)) {
          gesture.markFired(now);
          inputBus.emit(result.gesture);
        }
      }
    }
  }

  gestureHint.update(
    handData?.landmarks[0] ?? null,
    gestureResult,
    now,
    hand.getStatus(),
    { hasStream: camInput.getVideo().srcObject !== null, readyState: video.readyState }
  );

  gameState.tickElapsed(now);
  gameState.tickTimer(deltaMs);
  gameState.updateWeapons();
  gameState.tickCorruption(deltaMs);

  if (!gameState.get().isTimeUp && !gameState.get().isRunComplete && !gameState.get().isDead) {
    if (now - lastCorruptionEmit > 200) {
      if (gameState.get().corruption >= CONFIG.corruption.max) {
        inputBus.emit('corruption_full');
        lastCorruptionEmit = now;
      }
    }
  }

  if (gameState.get().isTimeUp && !gameState.get().isRunComplete) {
    running = false;
    inputBus.emit('time_up');
  }

  if (gameState.get().isRunComplete && !runEndShown) {
    running = false;
    inputBus.emit('run_complete');
  }

  if (gameState.get().isDead && !runEndShown) {
    running = false;
    inputBus.emit('player_died');
  }

  if (now - lastTrexRoar > 15000 && dinosaurs.dinosaurs.length > 0) {
    const trex = dinosaurs.dinosaurs.find((d) => d.species === 'trex');
    if (trex) {
      audio.playRoar();
      lastTrexRoar = now;
    } else if (dinosaurs.dinosaurs.length > 0 && Math.random() < 0.3) {
      audio.playRoar();
      lastTrexRoar = now;
    }
  }

  const state = gameState.get();
  orb.update(now, state, deltaMs);
  const ratio = state.corruption / CONFIG.corruption.max;
  foreground.update(now, deltaMs, ratio);

  const warnState = corruption.getWarnState();
  if (warnState !== lastHeartbeatState) {
    if (stopHeartbeat) {
      stopHeartbeat();
      stopHeartbeat = null;
    }
    lastHeartbeatState = warnState;
    if (warnState === 'warn') {
      stopHeartbeat = audio.startHeartbeat(900);
    } else if (warnState === 'crit') {
      stopHeartbeat = audio.startHeartbeat(550);
    }
  }
  if (state.corruption < CONFIG.corruption.warnThreshold && lastHeartbeatState !== 'normal') {
    if (stopHeartbeat) {
      stopHeartbeat();
      stopHeartbeat = null;
    }
    lastHeartbeatState = 'normal';
  }
  dinosaurs.orbPos.copy(orb.basePos);
  dinosaurs.update(now, deltaMs, orb.basePos);
  shockwaves.update(now);
  weapons.setCenter(orb.basePos);
  weapons.update(now, state, deltaMs);
  weapons.applyAutoMelee(now, dinosaurs.dinosaurs, (pos) => {
    const dx = pos[0] - orb.basePos.x;
    const dz = pos[2] - orb.basePos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist > 0.01) {
      const knock = CONFIG.weapons.autoMeleeKnockback;
      const target = dinosaurs.dinosaurs.find((d) => d.position === pos);
      if (target) {
        target.position[0] += (dx / dist) * knock;
        target.position[2] += (dz / dist) * knock;
      }
    }
  });
  corruption.update(now, state, deltaMs, ctx.camera, orb.basePos);

  ctx.renderer.render(ctx.scene, ctx.camera);
}

requestAnimationFrame(frame);
